/**
 * Get top categories by active ads count
 */
import { mainCategories } from './mainCategories';
import { productsRef } from '../../firebase/collections/refs';

/**
 * Category ids that are always pinned to the top of the list,
 * regardless of ad counts.
 */
const PINNED_CATEGORY_IDS = ['investment', 'wanted', 'bonuses'];

// Counts don't need to be real-time; the strip is now rendered on every page,
// so cache the aggregated result to avoid re-querying Firestore per navigation.
const CACHE_TTL_MS = 5 * 60 * 1000;
let topCategoriesCache = null;

/**
 * Synchronously return the cached full category list, if fresh data is
 * already available. Lets remounts (page navigation) skip the loading state.
 */
export const getCachedTopCategories = () =>
  topCategoriesCache && topCategoriesCache.value ? topCategoriesCache.value : null;

const fetchTopCategories = async () => {
  // Fetch all active products for each category
  const categoryCounts = await Promise.all(
    mainCategories.map(async (category) => {
      try {
        const snapshot = await productsRef()
          .where('category', '==', category.name)
          .where('status', '==', 'active')
          .where('moderationStatus', '==', 'approved')
          .get();

        return {
          id: category.id,
          name: category.name,
          icon: category.icon,
          subcategories: category.subcategories,
          order: category.order,
          count: snapshot.size,
        };
      } catch (error) {
        console.error(`Error counting ads for category ${category.id}:`, error);
        // Return category with 0 count if there's an error
        return {
          id: category.id,
          name: category.name,
          icon: category.icon,
          subcategories: category.subcategories,
          order: category.order,
          count: 0,
        };
      }
    })
  );

  // Pinned categories (Investment, Wanted) stay first in their natural order
  const pinned = PINNED_CATEGORY_IDS
    .map((id) => categoryCounts.find((c) => c.id === id))
    .filter(Boolean);

  // The rest sorted by count in descending order (most ads first)
  const sortedCategories = categoryCounts
    .filter((c) => !PINNED_CATEGORY_IDS.includes(c.id))
    .sort((a, b) => b.count - a.count);

  return [...pinned, ...sortedCategories];
};

/**
 * Get top categories: Investment and Wanted always come first,
 * the rest sorted by active ads count in descending order.
 * Results are cached for a few minutes and shared across pages.
 * @param {number} limit - Maximum number of categories to return (default: 14)
 * @returns {Promise<Array>} Array of categories with id, name, icon, and count
 */
export const getTopCategories = async (limit = 14) => {
  try {
    const now = Date.now();
    if (!topCategoriesCache || now > topCategoriesCache.expiresAt) {
      topCategoriesCache = {
        expiresAt: now + CACHE_TTL_MS,
        promise: fetchTopCategories(),
      };
      // Do not keep a rejected promise cached
      topCategoriesCache.promise.catch(() => {
        topCategoriesCache = null;
      });
    }
    const allCategories = await topCategoriesCache.promise;
    topCategoriesCache.value = allCategories;
    return allCategories.slice(0, limit);
  } catch (error) {
    console.error('Error in getTopCategories:', error);
    // Return categories sorted by order as fallback
    return mainCategories
      .slice(0, limit)
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        icon: cat.icon,
        subcategories: cat.subcategories,
        order: cat.order,
        count: 0,
      }));
  }
};
